<template>
    <validation-observer ref="form" v-slot="{ meta }">
        <modal
            :id="modalId"
            :title="title"
            scrollable
            ok-title="Save"
            :ok-disabled="!meta.valid || loading"
            @ok.prevent="save"
            @hide="hideModal"
            @shown="onModalShown"
        >
            <busy-modal :show="loading"></busy-modal>
            <b-form @submit.prevent="save" v-if="values">
                <b-row>
                    <b-col>
                        <label> Properties: </label>
                        <validation-provider name="properties" v-slot="{ errors }" rules="required" validateOnMount>
                            <properties-search-multiselect
                                v-model="values.properties"
                                :is-form="true"
                                :initial-value="initialValues.properties"
                                :properties="properties"
                                :error="errors[0]"
                            ></properties-search-multiselect>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <label for="rental-expenses-date"> Date: </label>
                        <datepicker
                            id="rental-expenses-date"
                            v-model="values.date"
                            :hide-header="true"
                            :display-format="userUnicodeDateFormat"
                            class="mb-3"
                        ></datepicker>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider
                            name="amount"
                            v-slot="{ errors }"
                            rules="validFloat|required"
                            validateOnMount
                        >
                            <form-input label="Amount:" v-model="values.amount" :error="errors[0]"></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <label for="rental-expenses-account-id"> Account: </label>
                        <validation-provider name="account_id" v-slot="{ errors }" :style="{ width: '95%' }">
                            <rental-expense-account-select
                                v-model="values.account_id"
                                :error="errors[0]"
                            ></rental-expense-account-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="assignee" v-slot="{ errors }">
                            <rental-expense-assignee-select
                                v-model="values.assignee"
                                :error="errors[0]"
                            ></rental-expense-assignee-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="vendor_id" v-slot="{ errors }">
                            <rental-expense-vendor-select
                                v-model="values.vendor_id"
                                :error="errors[0]"
                            ></rental-expense-vendor-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="description" rules="required" v-slot="{ errors }" validateOnMount>
                            <form-textarea
                                label="Description:"
                                v-model="values.description"
                                :error="errors[0]"
                            ></form-textarea>
                        </validation-provider>
                    </b-col>
                </b-row>
            </b-form>
        </modal>
    </validation-observer>
</template>

<script>
import { mapGetters } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import isNil from 'lodash/isNil';
import API from '../../../api';
import { formatResponseError } from '../../../utils/api';
import BusyModal from '../../generic/busy-modal/busy-modal.vue';
import Datepicker from '../../generic/datepicker/datepicker.vue';
import FormCheckbox from '../../generic/form-checkbox/form-checkbox.vue';
import FormInput from '../../generic/form-input/form-input.vue';
import FormTextarea from '../../generic/form-textarea/form-textarea.vue';
import Modal from '../../generic/modal/modal.vue';
import RentalExpenseAccountSelect from '../common/rental-expense-account-select.vue';
import RentalExpenseAssigneeSelect from '../common/rental-expense-assignee-select.vue';
import PropertiesSearchMultiselect from '../../generic/properties-search-multiselect/properties-search-multiselect.vue';
import RentalExpenseVendorSelect from '../common/rental-expense-vendor-select.vue';

export default {
    name: 'RentalExpenseDetailsModalForm',
    components: {
        BusyModal,
        Datepicker,
        FormCheckbox,
        FormInput,
        FormTextarea,
        Modal,
        RentalExpenseAccountSelect,
        RentalExpenseAssigneeSelect,
        PropertiesSearchMultiselect,
        RentalExpenseVendorSelect,
    },
    props: {
        modalId: String,
        create: {
            type: Function,
            default: () => Promise.resolve(),
        },
        update: {
            type: Function,
            default: () => Promise.resolve(),
        },
        initialValues: {
            type: Object,
            default: () => ({}),
        },
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat']),
        ...mapGetters('workOrders', ['accountExpenses']),
        title() {
            return this.values && this.values.id ? 'Edit Rental Expense' : 'Create Rental Expense';
        },
    },
    data() {
        return {
            loading: false,
            properties: [],
            values: null,
        };
    },
    mounted() {
        (async () => {
            this.loading = true;
            try {
                await Promise.all([this.fetchAccountExpenses(), this.fetchVendors(), this.fetchProperties()]);
                this.values = this.getInitialValues();
            } finally {
                this.loading = false;
            }
        })();
    },
    methods: {
        getInitialValues() {
            return {
                id: this.initialValues.id || null,
                date: this.initialValues.date
                    ? format(parseISO(this.initialValues.date), 'yyyy-MM-dd')
                    : format(new Date(), 'yyyy-MM-dd'),
                amount: this.initialValues.amount || '',
                account_id:
                    this.initialValues.account_id ||
                    (this.accountExpenses.length > 0 ? this.accountExpenses[0].id : null),
                vendor_id: this.initialValues.vendor_id || null,
                assignee: isNil(this.initialValues.assignee) ? 1 : this.initialValues.assignee,
                description: this.initialValues.description || '',
                properties: this.initialValues.properties || [],
            };
        },
        hideModal() {
            this.$bvModal.hide(this.modalId);
        },
        async fetchAccountExpenses() {
            try {
                await this.$store.dispatch('workOrders/fetchAccountExpenses');
            } catch (err) {
                this.errorToast({ text: 'Could not load account expenses.' });
            }
        },
        async fetchVendors() {
            try {
                await this.$store.dispatch('workOrders/fetchVendors');
            } catch (err) {
                this.errorToast({ text: 'Could not load vendors.' });
            }
        },
        async fetchProperties() {
            try {
                const response = await API.get(`/app/rental-expense/rental-expense/properties/?with_disabled=true`);
                this.properties = response.data;
            } catch {
                this.errorToast({ text: 'Could not load properties.' });
            }
        },
        async save() {
            this.loading = true;

            try {
                await (this.values.id ? this.update(this.values) : this.create(this.values));
                this.$emit('success-save');
                this.$nextTick(() => {
                    this.hideModal();
                });
            } catch (err) {
                const { generic, fields } = formatResponseError(err);

                if (generic) {
                    this.errorToast({ text: generic });
                }
                if (!isEmpty(fields)) {
                    this.$refs.form.setErrors(fields);

                    const hasPropertiesError = Object.keys(fields).some((field) => field.startsWith('properties'));
                    if (!fields.hasOwnProperty('properties') && hasPropertiesError) {
                        this.$refs.form.setErrors({ properties: 'Properties are not valid.' });
                    }
                }
            } finally {
                this.loading = false;
            }
        },
        onModalShown() {
            this.$refs.form.setValues(this.getInitialValues());
            this.$refs.form.validate();
        },
    },
    watch: {
        initialValues() {
            this.values = this.getInitialValues();
        },
        values: {
            handler(val) {
                this.$refs.form.setValues(val);
            },
            deep: true,
        },
    },
};
</script>
